import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AccountsSubNav from '../components/AccountsSubNav';
import { appToast } from '../../../../components/AppToast';

const STORAGE_KEY = 'bridge_erp_cust_cash';

export default function CustCashEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    mobile: '',
    nameAddress: '',
    gst: '',
    status: 'Active'
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const list = JSON.parse(saved);
          const found = list.find(v => String(v.id) === String(id));
          if (found) {
            setForm(found);
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [id, isEdit]);

  const patchForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = (options = { exitAfter: true }) => {
    if (!form.mobile.trim()) {
      appToast.error('Mobile No. is required.');
      return;
    }

    setIsSaving(true);

    try {
      const saved = localStorage.getItem(STORAGE_KEY) || '[]';
      let list = JSON.parse(saved);

      if (isEdit) {
        list = list.map(v => String(v.id) === String(id) ? { ...form } : v);
        appToast.success('Cash Customer updated successfully!');
      } else {
        const newCust = {
          ...form,
          id: String(Date.now()),
          created_at: new Date().toISOString()
        };
        list = [newCust, ...list];
        appToast.success('Cash Customer created successfully!');
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));

      if (options.exitAfter) {
        setTimeout(() => {
          navigate('/masters/acheads/cust-cash/list');
        }, 350);
      } else {
        setForm({
          mobile: '',
          nameAddress: '',
          gst: '',
          status: 'Active'
        });
      }
    } catch (e) {
      appToast.error('Failed to save Cash Customer.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Cash Customer' : 'Create Cash Customer'}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <AccountsSubNav />
          <Link
            to="/masters/acheads/cust-cash/list"
            className="inline-flex items-center gap-1 text-xs font-bold text-on-surface-variant hover:text-primary transition"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back</span>
          </Link>
        </div>
      </section>

      {/* Main Grid: Form Left, Instructions Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Form: Column span 8 */}
        <div className="lg:col-span-8 rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden shadow-xs">
          <div className="bg-surface-container-low/60 px-6 py-3 border-b border-outline-variant/25 text-sm font-bold text-on-surface">
            Cash Customer
          </div>

          <div className="p-6 flex flex-col gap-5">
            {/* Mobile No. */}
            <label className="grid grid-cols-12 gap-4 items-center">
              <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                Mobile No.<span className="text-error"> *</span>
              </span>
              <input
                type="text"
                value={form.mobile}
                onChange={(e) => patchForm('mobile', e.target.value)}
                placeholder="Enter Mobile Number..."
                required
                className="col-span-9 h-10 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
            </label>

            {/* Name/Address */}
            <label className="grid grid-cols-12 gap-4 items-start">
              <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant pt-2.5">
                Name/Address
              </span>
              <textarea
                value={form.nameAddress}
                onChange={(e) => patchForm('nameAddress', e.target.value)}
                placeholder="Enter Cash Customer Name and Address..."
                rows={4}
                className="col-span-9 rounded-xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition resize-y"
              />
            </label>

            {/* GST No. */}
            <label className="grid grid-cols-12 gap-4 items-center">
              <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                GST No.
              </span>
              <input
                type="text"
                value={form.gst}
                onChange={(e) => patchForm('gst', e.target.value)}
                placeholder="Enter GSTIN if available..."
                className="col-span-9 h-10 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
            </label>
          </div>
        </div>

        {/* Right Info Box: Column span 4 */}
        <div className="lg:col-span-4 rounded-2xl border border-primary/20 bg-primary/5 p-6 flex flex-col gap-4 text-on-surface select-none">
          <div className="flex items-center gap-2 text-primary">
            <span className="material-symbols-outlined text-lg">info</span>
            <span className="text-xs font-black uppercase tracking-wider">Retail Customer Notes</span>
          </div>
          <ul className="text-xs text-on-surface-variant flex flex-col gap-3 list-disc pl-4 leading-relaxed font-semibold">
            <li>Use this feature to track retail customers</li>
            <li>Ledger accounts are <span className="font-extrabold text-primary">NOT</span> created for cash customers</li>
            <li>Entries are automatically created/updated when you save cash sale transactions</li>
            <li>You can add/delete retail customers from masters</li>
            <li>You can also send messages to retail customers</li>
          </ul>
        </div>

      </div>

      {/* Footer Actions Bar */}
      <section className="flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-4 shadow-sm">
        <button
          type="button"
          onClick={() => navigate('/masters/acheads/cust-cash/list')}
          className="rounded-xl bg-secondary-container px-6 py-2.5 text-xs font-bold text-on-secondary-container transition hover:brightness-95 cursor-pointer"
        >
          Cancel
        </button>

        {!isEdit && (
          <button
            type="button"
            disabled={isSaving}
            onClick={() => handleSave({ exitAfter: false })}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-6 py-2.5 text-xs font-bold text-primary transition hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            Save &amp; Continue
          </button>
        )}

        <button
          type="button"
          disabled={isSaving}
          onClick={() => handleSave({ exitAfter: true })}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-container px-8 py-2.5 text-xs font-bold text-on-primary shadow-lg shadow-primary/20 transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          <span className="material-symbols-outlined text-base">save</span>
          <span>{isSaving ? 'Saving...' : 'Save & Exit'}</span>
        </button>
      </section>
    </div>
  );
}
